import { useEffect, useRef, useState } from 'react'
import { Eraser } from 'lucide-react'
import type { BiometricSample } from '../types'

interface Props {
  onChange: (out: { imageDataUrl: string | null; biometricData: BiometricSample[] }) => void
}

const CANVAS_WIDTH = 600
const CANVAS_HEIGHT = 200

/**
 * Captura una firma dibujada a mano alzada en canvas + time-series de
 * eventos pointer para análisis biométrico (presión, velocidad, trazo).
 * El time-series se guarda en sgw_electronic_signatures.biometric_data
 * para pericia caligráfica futura.
 */
export function DrawnSignatureCapture({ onChange }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [isDrawing, setIsDrawing] = useState(false)
  const [biometricData, setBiometricData] = useState<BiometricSample[]>([])
  const [hasContent, setHasContent] = useState(false)
  const startTimeRef = useRef<number>(0)
  const lastPointRef = useRef<{ x: number; y: number } | null>(null)

  // Init canvas
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')!
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    ctx.strokeStyle = '#0a2540'
    ctx.lineWidth = 2.2
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
  }, [])

  const getPointerPos = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = canvasRef.current!.getBoundingClientRect()
    const scaleX = canvasRef.current!.width / rect.width
    const scaleY = canvasRef.current!.height / rect.height
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
      pressure: e.pressure || 0.5,
      pointerType: e.pointerType,
    }
  }

  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.preventDefault()
    canvasRef.current?.setPointerCapture(e.pointerId)
    if (biometricData.length === 0) startTimeRef.current = performance.now()
    const p = getPointerPos(e)
    lastPointRef.current = { x: p.x, y: p.y }
    setBiometricData(prev => [...prev, {
      x: p.x, y: p.y, t: performance.now() - startTimeRef.current,
      pressure: p.pressure, pointerType: p.pointerType,
    }])
    setIsDrawing(true)
  }

  const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return
    const p = getPointerPos(e)
    const ctx = canvasRef.current!.getContext('2d')!
    const last = lastPointRef.current
    if (last) {
      ctx.beginPath()
      ctx.moveTo(last.x, last.y)
      ctx.lineTo(p.x, p.y)
      ctx.lineWidth = 1.5 + p.pressure * 1.5
      ctx.stroke()
    }
    lastPointRef.current = { x: p.x, y: p.y }
    setBiometricData(prev => [...prev, {
      x: p.x, y: p.y, t: performance.now() - startTimeRef.current,
      pressure: p.pressure, pointerType: p.pointerType,
    }])
    setHasContent(true)
  }

  const onPointerUp = () => {
    setIsDrawing(false)
    lastPointRef.current = null
    if (canvasRef.current && hasContent) {
      const dataUrl = canvasRef.current.toDataURL('image/png')
      onChange({ imageDataUrl: dataUrl, biometricData })
    }
  }

  const handleClear = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')!
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    setBiometricData([])
    setHasContent(false)
    onChange({ imageDataUrl: null, biometricData: [] })
  }

  return (
    <div className="space-y-2">
      <div className="rounded-lg border-2 border-dashed border-legal-300 dark:border-legal-600 bg-white overflow-hidden touch-none">
        <canvas
          ref={canvasRef}
          width={CANVAS_WIDTH}
          height={CANVAS_HEIGHT}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          className="w-full h-auto cursor-crosshair touch-none"
          style={{ aspectRatio: `${CANVAS_WIDTH} / ${CANVAS_HEIGHT}` }}
        />
      </div>
      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
        <span>
          {hasContent
            ? `${biometricData.length} puntos capturados`
            : 'Dibujá tu firma con mouse, dedo o lápiz'}
        </span>
        <button
          type="button"
          onClick={handleClear}
          disabled={!hasContent}
          className="flex items-center gap-1 px-2 py-1 rounded text-xs text-legal-600 hover:bg-legal-100 dark:text-legal-300 dark:hover:bg-legal-700 disabled:opacity-30"
        >
          <Eraser size={12} />
          Borrar
        </button>
      </div>
      {hasContent && (
        <p className="text-xs text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/20 rounded p-2">
          Se guardan datos biométricos del trazo (posición, presión, tiempo) para pericia caligráfica futura.
        </p>
      )}
    </div>
  )
}
